import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useTranslation } from '@hooks/useTranslation';

import WorkpayLogoIconSvg from '@assets/logo-icon.svg';
import WorkpayLogoSvg from '@assets/icons/workpayLogo.svg';
import SidebarChevronIcon from '@assets/icons/SidebarChevronIcon';
import ChevronDownSmallIcon from '@assets/icons/ChevronDownSmallIcon';
import XIcon from '@assets/icons/XIcon';

// ============================================
// Sidebar Navigation
// Uses global layout classes from _layout.scss
// ============================================

export interface SidebarSubMenuItem {
  id: string;
  label: string;
  href: string;
}

export interface SidebarMenuItem {
  id: string;
  label: string;
  icon: ReactNode;
  href?: string;
  children?: SidebarSubMenuItem[];
  /** Set for GROUP-type entries from the navigation API. Used to prefetch
   *  and identify sub-module content on the group's landing page. */
  groupIdentifier?: string;
}

export interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  menuItems: SidebarMenuItem[];
  activeItemId: string;
  /** Items pinned to the bottom of the sidebar (above the fold) */
  bottomItems?: SidebarMenuItem[];
  /** Below the lg breakpoint, the sidebar is a slide-in drawer. When true,
   *  apply `.sidebar--mobile-open` to bring it into view. Ignored on desktop. */
  mobileOpen?: boolean;
  /** Mobile-only: close handler for the drawer's × button. Ignored on desktop. */
  onMobileClose?: () => void;
}

// Workpay logo — uses full wordmark when expanded, icon-only when collapsed
function WorkpayLogo({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  return (
    <div className="sidebar__logo">
      <img
        src={collapsed ? WorkpayLogoIconSvg : WorkpayLogoSvg}
        alt={t('K_APP_NAME', 'WorkPay')}
        height={collapsed ? 20 : 24}
        style={{ objectFit: 'contain' }}
      />
    </div>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  menuItems,
  activeItemId,
  bottomItems,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const { t } = useTranslation();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);

  // Auto-open the parent menu that contains the active sub-item
  const resolvedOpenId = (() => {
    if (openMenuId !== null) return openMenuId;
    for (const item of menuItems) {
      if (item.children?.some((child) => child.id === activeItemId)) {
        return item.id;
      }
    }
    return null;
  })();

  return (
    <aside
      className={`sidebar${collapsed ? ' sidebar--collapsed' : ''}${mobileOpen ? ' sidebar--mobile-open' : ''}`}
      // When the drawer is open (mobile only), it behaves as a modal — give it
      // dialog semantics so screen readers announce it. On desktop it's the
      // permanent nav landmark (plain <aside>), so we skip the role there.
      {...(mobileOpen
        ? {
            role: 'dialog' as const,
            'aria-modal': true,
            'aria-label': t('K_ARIA_NAV_MENU', 'Navigation menu'),
          }
        : {})}
    >
      <div className="sidebar__header">
        <WorkpayLogo collapsed={collapsed} />

        {/* Collapse/expand toggle — positioned inside header so it sits at the top-right edge */}
        <button
          className="sidebar__toggle"
          onClick={onToggle}
          aria-label={collapsed ? t('K_ARIA_EXPAND_SIDEBAR', 'Expand sidebar') : t('K_ARIA_COLLAPSE_SIDEBAR', 'Collapse sidebar')}
          type="button"
        >
          <SidebarChevronIcon direction={collapsed ? 'right' : 'left'} />
        </button>

        {/* Mobile-only close (×) — visible inside the drawer since the
            full-width drawer hides the backdrop and the desktop chevron. */}
        {onMobileClose && (
          <button
            type="button"
            className="sidebar__close"
            onClick={onMobileClose}
            aria-label={t('K_ARIA_CLOSE_NAV_MENU', 'Close navigation menu')}
          >
            <XIcon />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="sidebar__nav">
        {menuItems.map((item) => {
          const hasChildren = item.children && item.children.length > 0;
          const isOpen = resolvedOpenId === item.id;
          const isActive = item.id === activeItemId;
          const hasActiveChild = item.children?.some((child) => child.id === activeItemId);

          // Menu item with children (expandable)
          if (hasChildren) {
            return (
              <div
                key={item.id}
                className={`sidebar-menu${isOpen ? ' sidebar-menu--open' : ''}${hasActiveChild ? ' sidebar-menu--active' : ''}`}
              >
                <button
                  className="sidebar-menu__header"
                  onClick={() => setOpenMenuId(isOpen ? null : item.id)}
                  type="button"
                  title={collapsed ? item.label : undefined}
                >
                  <span className="sidebar-menu__icon">{item.icon}</span>
                  <span className="sidebar-menu__label">{item.label}</span>
                  <span className="sidebar-menu__chevron">
                    <ChevronDownSmallIcon />
                  </span>
                </button>

                {/* Sub-menu items */}
                {isOpen && !collapsed && item.children && (
                  <div className="sidebar-submenu">
                    {item.children.map((child) => (
                      <Link
                        key={child.id}
                        to={child.href}
                        className={`sidebar-submenu__item${child.id === activeItemId ? ' sidebar-submenu__item--active' : ''}`}
                        title={child.label}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          // Simple menu item (no children)
          return (
            <Link
              key={item.id}
              to={item.href!}
              className={`sidebar-menu${isActive ? ' sidebar-menu--selected' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="sidebar-menu__icon">{item.icon}</span>
              <span className="sidebar-menu__label">{item.label}</span>
            </Link>
          );
        })}
      </nav>

      {/* Bottom section */}
      {bottomItems && bottomItems.length > 0 && (
        <div className="sidebar__bottom">
          {bottomItems.map((item) => (
            <Link
              key={item.id}
              to={item.href!}
              className={`sidebar-menu${item.id === activeItemId ? ' sidebar-menu--selected' : ''}`}
              title={collapsed ? item.label : undefined}
            >
              <span className="sidebar-menu__icon">{item.icon}</span>
              <span className="sidebar-menu__label">{item.label}</span>
            </Link>
          ))}
        </div>
      )}
    </aside>
  );
}
